import React, { useCallback, useState } from "react";
import cx from "classnames";
import { editorHeader } from "../_styles/header";
import { editorSection, editorSectionSeparator, editorSectionTitle } from "../_styles/section";
import { H4 } from "@ui/Text";
import t from "../../../../../../../../i18n";
import { AddCircleLine, FileTextLine, InformationLine } from "@ui/IconV2/react";
import { colors, spacings } from "@ui/Theme";
import { Text } from "@ui/Text";
import { css } from "@linaria/core";
import Button from "@ui/Button";
import { useCurrentEditorBlock } from "../../../../../hook/context/gui";
import type { BlockDiagramModelT } from "../../../../../schema/block/diagram";
import { Annotation } from "./Annotation";
import { AnnotationSettings } from "./AnnotationSettings";
import { useSelectedDiagramAnnotation } from "../../hook/useSelectedDiagramAnnotation";
import { BlockPosition } from "../BlockPosition";
import { BlockDataTitle } from "../BlockDataTitle";
import { BlockFile } from "../BlockFile";
import Menu from "@ui/Menu";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import { useDashboardEditorContext } from "../../../../../hook/context/editor";
import { AnnotationIcon } from "./AnnotationIcon";
import { hashCode } from "diagnostics-lib/object";
import { useAvailableResources } from "../../../../../hook/useAvailableResources";
import { getAvailableResourceKind } from "../../../../../utils/getAvailableResourceKind";
import Details from "@ui/Details";
import { ErrorDetails } from "../_styles/error";
import Switch from "@ui/Switch";
import TooltipV2 from "@ui/TooltipV2";
import RadioButton from "@ui/RadioButton";
import { BlockDateRangeSelector } from "../BlockDateRangeSelector";
import { Duration } from "../../../../../schema/period";

export function DiagramEditor({ projectId }: { projectId: string }) {
  const editor = useDashboardEditorContext();
  const availableResources = useAvailableResources({ projectId });
  const block = useCurrentEditorBlock<BlockDiagramModelT>("DIAGRAM");
  const [selectedAnnotationIndex, selectAnnotationIndex] = useSelectedDiagramAnnotation({
    scope: block?.id ?? "",
  });

  const addAnnotation = useCallback(
    (type: "area" | "point" | "label") => async () => {
      if (!block) return;
      selectAnnotationIndex(block.config.layers.length);
      let position = [0.5, 0.5, 0, 0];
      switch (type) {
        case "label":
          position = [0.5, 0.5, -1, -1];
          break;
        case "point":
          position = [0.5, 0.5, 0, 0];
          break;
        case "area":
          position = [0.5, 0.5, 0.1, 0.1];
          break;
        default:
          break;
      }

      await editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          layers: [
            ...block.config.layers,
            {
              name: null,
              position,
              anchor: {
                x: "center",
                y: "top",
              },
              encodings: [],
              thresholds: [],
              evolutions: [],
              anchorRelativePositions: [],
              showLabel: true,
              showDescription: true,
            },
          ],
        },
      });
    },
    [block, editor, selectAnnotationIndex],
  );

  const getKind = useCallback(
    (identifier: string) => getAvailableResourceKind(availableResources, identifier) ?? null,
    [availableResources],
  );

  const updateLiveUpdate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!block) return;
      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          live: e.target.checked,
        },
      });
    },
    [block, editor],
  );

  const buildUpdateAnnotationDisplay = useCallback(
    (name: "show_all" | "show_role") => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!block) return;
      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          [name]: e.target.checked,
        },
      });
    },
    [block, editor],
  );

  const [displayedValue, setDisplayedValue] = useState<"analysis" | "period">(() =>
    block?.config.aggregate ? "period" : "analysis",
  );

  const resetPeriod = useCallback(() => {
    if (!block) return;
    void editor?.updateBlock({
      ...block,
      data: {
        ...block.data,
        period: "ALL",
      },
      config: {
        ...block.config,
        live: block.config.live,
        aggregate: false,
      },
    });
  }, [block, editor]);

  const updateBlockWithPeriod = useCallback(() => {
    if (!block) return;
    void editor?.updateBlock({
      ...block,
      data: {
        ...block.data,
        period: "P1M",
      },
      config: {
        ...block.config,
        aggregate: true,
        layers: block.config.layers.map(layer => ({
          ...layer,
          evolutions: [],
        })),
      },
    });
  }, [block, editor]);

  const allowLiveData = Duration.is(block?.data.period) || !block?.data.period;

  if (!block) return null;

  return (
    <div>
      <header className={cx(editorHeader, editorSection)}>
        <H4>
          {t("project.dashboard.editor.gui.title", t("project.dashboard.block.kind", "DIAGRAM"))}
        </H4>
      </header>
      <section className={editorSection}>
        <BlockPosition block={block} />
      </section>
      <section className={editorSection}>
        <BlockDataTitle key={block.id} block={block} />
      </section>
      <section className={cx(editorSection, blockFileSection)}>
        <BlockFile projectId={projectId} block={block} />
      </section>
      <div className={editorSectionSeparator} />
      <section className={editorSection}>
        <Text type="body1SemiBold" className={displayedValueTitle}>
          {t("project.dashboard.editor.gui.section.displayed_value.title")}
          <TooltipV2
            overlay={t("project.dashboard.editor.gui.section.displayed_value.description")}
          >
            <span>
              <InformationLine color={colors.carbon800} />
            </span>
          </TooltipV2>
        </Text>
        <div className={displayedValueSelector}>
          <RadioButton
            name="displayedValue"
            checked={displayedValue === "analysis"}
            onChange={() => {
              setDisplayedValue("analysis");
              resetPeriod();
            }}
          >
            {t("project.dashboard.editor.gui.section.displayed_value.analysis")}
          </RadioButton>
          <RadioButton
            name="displayedValue"
            checked={displayedValue === "period"}
            onChange={() => {
              setDisplayedValue("period");
              updateBlockWithPeriod();
            }}
          >
            {t("project.dashboard.editor.gui.section.displayed_value.period")}
          </RadioButton>
        </div>
        {displayedValue === "period" ? (
          <BlockDateRangeSelector projectId={projectId} block={block} defaultPreset="P1M" />
        ) : (
          <div />
        )}
        <Switch
          disabled={!allowLiveData}
          checked={Boolean(block.config.live)}
          onChange={updateLiveUpdate}
          tooltipOverlay={t("project.dashboard.block.diagram.display.last_analysis.detail")}
        >
          {t("project.dashboard.block.diagram.display.last_analysis")}
        </Switch>
      </section>
      <div className={editorSectionSeparator} />
      <section className={editorSection}>
        <div className={cx(editorSectionTitle, fileEditorSectionTitle)}>
          <Text type="body1SemiBold">
            {t(
              "project.dashboard.editor.gui.section.title.annotation",
              block?.config.layers.length ?? 0,
            )}
          </Text>
          <Menu
            overlay={
              <div>
                <OptionV2 onClick={addAnnotation("area")} closeOnClick>
                  <AnnotationIcon width={1} height={1} slot="icon" />
                  <FragmentSlot slot="text">
                    {t("project.dashboard.editor.gui.section.annotation_settings.area")}
                  </FragmentSlot>
                </OptionV2>
                <OptionV2 onClick={addAnnotation("point")} closeOnClick>
                  <AnnotationIcon width={0} height={0} slot="icon" />
                  <FragmentSlot slot="text">
                    {t("project.dashboard.editor.gui.section.annotation_settings.dot")}
                  </FragmentSlot>
                </OptionV2>
                <OptionV2 onClick={addAnnotation("label")} closeOnClick>
                  <FragmentSlot slot="icon">
                    <FileTextLine color={colors.carbon800} />
                  </FragmentSlot>
                  <FragmentSlot slot="text">
                    {t("project.dashboard.editor.gui.section.annotation_settings.label_only")}
                  </FragmentSlot>
                </OptionV2>
              </div>
            }
          >
            <Button
              variant="tertiary"
              slim
              leftIcon={<AddCircleLine />}
              disabled={!block?.config.url}
            >
              {t("project.dashboard.editor.gui.section.annotation_settings.action")}
            </Button>
          </Menu>
        </div>
        <div className={annotations}>
          {block?.config.layers.map((layer, i) => (
            <Annotation
              projectId={projectId}
              key={hashCode({ ...layer, __index: i })}
              annotation={layer}
              kind={getKind(layer.encodings[0]?.identifier ?? "")}
              index={i}
              selected={selectedAnnotationIndex === i}
              onClick={() => selectAnnotationIndex(i)}
            />
          ))}
        </div>
        {selectedAnnotationIndex !== null && block?.config.layers[selectedAnnotationIndex] && (
          <>
            <div className={editorSectionSeparator} />
            <AnnotationSettings
              projectId={projectId}
              block={block}
              annotation={block?.config.layers[selectedAnnotationIndex]}
            />
          </>
        )}
      </section>
      <div className={editorSectionSeparator} />
      <section className={editorSection}>
        <Text type="body1SemiBold">{t("project.dashboard.block.diagram.display.title")}</Text>
        <Switch
          checked={Boolean(block.config.show_all)}
          onChange={buildUpdateAnnotationDisplay("show_all")}
        >
          {t("project.dashboard.block.diagram.display.all")}
        </Switch>
        <Switch
          checked={block.config.show_role}
          onChange={buildUpdateAnnotationDisplay("show_role")}
        >
          {t("project.dashboard.block.diagram.display.roles")}
        </Switch>
      </section>
      {editor?.error &&
        !editor.errors?.some(e => e.context.some(({ key }) => key === "thresholds")) && (
          <section className={editorSection}>
            <Details className={ErrorDetails} text={editor?.error} />
          </section>
        )}
    </div>
  );
}

const blockFileSection = css`
  margin-top: ${spacings.m};
`;

const fileEditorSectionTitle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const displayedValueTitle = css`
  display: flex;
  gap: ${spacings.xxs};
  align-items: center;

  span {
    display: inline-flex;
    align-items: center;
  }
`;

const displayedValueSelector = css`
  display: flex;
  gap: ${spacings.m};
`;

const annotations = css`
  max-height: 250px;
  overflow-y: auto;
  position: relative;
`;
